<section class="py-24 bg-[#0a0a0f] text-white border-b border-white/5 relative">
	<div class="container mx-auto px-6 max-w-7xl">
		<div class="text-center mb-16">
			<h2 class="text-4xl font-bold mb-4">
				Why Teams Love <span class="text-primary">OS Studio</span>
			</h2>
			<p class="text-gray-400 text-lg max-w-2xl mx-auto">
				Enterprise-grade capabilities, automated for speed and security.
			</p>
		</div>

		<div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-primary/10 border border-primary/20 group-hover:bg-primary/20 group-hover:border-primary/40 transition-all shadow-[0_0_15px_rgba(124,58,237,0.1)] group-hover:shadow-[0_0_20px_rgba(124,58,237,0.3)]"
					>
						<svg
							class="h-6 w-6 text-primary"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 10V3L4 14h7v7l9-11h-7z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-primary-300 transition-colors"
					>
						AI-Powered Architecture Design
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Our AI understands patterns from 10,000+ systems. It
						designs yours optimized for scalability, cost, security,
						and performance.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-neon-cyan/10 border border-neon-cyan/20 group-hover:bg-neon-cyan/20 group-hover:border-neon-cyan/40 transition-all shadow-[0_0_15px_rgba(0,242,234,0.1)] group-hover:shadow-[0_0_20px_rgba(0,242,234,0.3)]"
					>
						<svg
							class="h-6 w-6 text-neon-cyan"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-neon-cyan transition-colors"
					>
						Conversational Requirements
					</h3>
					<p class="text-gray-400 leading-relaxed">
						No lengthy documentation. Just have a conversation with
						AI. It asks the right questions and builds complete
						specifications.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-purple-500/10 border border-purple-500/20 group-hover:bg-purple-500/20 group-hover:border-purple-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-purple-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-purple-300 transition-colors"
					>
						Production-Ready Code
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Every line follows best practices. Security hardening,
						error handling, testing, and documentation—all
						automatic.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-blue-500/10 border border-blue-500/20 group-hover:bg-blue-500/20 group-hover:border-blue-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-blue-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M14 10h-2m0 0H8m4 0V6m0 4v4m0-4h2m0 0h4"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-blue-300 transition-colors"
					>
						Intelligent Deployment
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Deploy to AWS, Azure, GCP, or on-premises. Automatic
						environment configuration, migrations, and health
						checks.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 group-hover:bg-emerald-500/20 group-hover:border-emerald-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-emerald-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-emerald-300 transition-colors"
					>
						24/7 Operating System
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Continuous monitoring, performance optimization,
						security updates, and cost optimization happen
						automatically around the clock.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-red-500/10 border border-red-500/20 group-hover:bg-red-500/20 group-hover:border-red-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-red-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-red-300 transition-colors"
					>
						Enterprise Security
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Built-in encryption, RBAC, audit logging. Automatic
						compliance with GDPR, HIPAA, SOC2.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-orange-500/10 border border-orange-500/20 group-hover:bg-orange-500/20 group-hover:border-orange-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-orange-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 10V3L4 14h7v7l9-11h-7z"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-orange-300 transition-colors"
					>
						AI-Powered Operations
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Anomaly detection, predictive maintenance, resource
						optimization—all AI-driven. Your system gets smarter
						every day.
					</p>
				</div>
			</div>

			<div class="flex gap-6 group">
				<div class="flex-shrink-0">
					<div
						class="flex items-center justify-center h-14 w-14 rounded-xl bg-pink-500/10 border border-pink-500/20 group-hover:bg-pink-500/20 group-hover:border-pink-500/40 transition-all"
					>
						<svg
							class="h-6 w-6 text-pink-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3v-6"
							/>
						</svg>
					</div>
				</div>
				<div>
					<h3
						class="text-xl font-bold mb-3 text-white group-hover:text-pink-300 transition-colors"
					>
						Continuous Evolution
					</h3>
					<p class="text-gray-400 leading-relaxed">
						Iterate, extend, and customize everything. Your system
						grows with your business needs.
					</p>
				</div>
			</div>
		</div>
	</div>
</section>
